import { applyMove, newGame, type Game } from '../game/game.js'
import { resultOf, type GameResult } from '../game/status.js'
import { INITIAL_FEN } from '../position/fen.js'
import { WHITE } from '../primitives/color.js'
import { err, ok, type Result } from '../result.js'
import { parseSan, type SanError } from './san.js'

/**
 * One move in the movetext, with everything hanging off it: annotation glyphs,
 * the comment that follows it, and any alternative lines someone recorded.
 */
export interface PgnNode {
  readonly san: string
  readonly nags: readonly number[]
  readonly comment: string | null
  readonly variations: readonly (readonly PgnNode[])[]
}

export interface PgnGame {
  readonly headers: ReadonlyMap<string, string>
  /** A comment before the first move, if the file had one. */
  readonly preComment: string | null
  readonly moves: readonly PgnNode[]
  readonly result: GameResult
}

export type PgnError =
  | { readonly kind: 'unterminatedComment'; readonly index: number }
  | { readonly kind: 'unterminatedVariation'; readonly index: number }
  | { readonly kind: 'unterminatedHeader'; readonly index: number }
  | { readonly kind: 'unexpectedCharacter'; readonly character: string; readonly index: number }
  | { readonly kind: 'noGames' }
  | { readonly kind: 'badSetupFen'; readonly fen: string }
  | { readonly kind: 'illegalMove'; readonly san: string; readonly ply: number; readonly cause: SanError }
  | { readonly kind: 'nullMoveUnsupported'; readonly ply: number }

export const describePgnError = (error: PgnError): string => {
  switch (error.kind) {
    case 'unterminatedComment':
      return `comment opened at ${error.index} is never closed`
    case 'unterminatedVariation':
      return `variation opened at ${error.index} is never closed`
    case 'unterminatedHeader':
      return `tag pair at ${error.index} is never closed`
    case 'unexpectedCharacter':
      return `unexpected ${JSON.stringify(error.character)} at ${error.index}`
    case 'noGames':
      return 'no games found'
    case 'badSetupFen':
      return `unparseable FEN header ${JSON.stringify(error.fen)}`
    case 'illegalMove':
      return `move ${error.ply + 1} (${error.san}) is not legal: ${error.cause.kind}`
    case 'nullMoveUnsupported':
      return `null move at ply ${error.ply + 1} cannot be replayed`
  }
}

interface Cursor {
  readonly text: string
  index: number
}

const RESULTS: readonly GameResult[] = ['1-0', '0-1', '1/2-1/2', '*']

const atLineStart = (cursor: Cursor): boolean =>
  cursor.index === 0 || cursor.text[cursor.index - 1] === '\n'

/**
 * Whitespace, `;` line comments, and `%` escape lines — all of which may appear
 * anywhere and mean nothing. `%` only counts in column 1, which is why the line
 * position is tracked rather than assumed.
 */
const skipTrivia = (cursor: Cursor): void => {
  const { text } = cursor
  for (;;) {
    const char = text[cursor.index]
    if (char === undefined) return
    if (char === ' ' || char === '\t' || char === '\r' || char === '\n') {
      cursor.index += 1
      continue
    }
    if (char === ';' || (char === '%' && atLineStart(cursor))) {
      while (cursor.index < text.length && text[cursor.index] !== '\n') cursor.index += 1
      continue
    }
    return
  }
}

const readBraceComment = (cursor: Cursor): Result<string, PgnError> => {
  const start = cursor.index
  cursor.index += 1 // consume '{'
  const end = cursor.text.indexOf('}', cursor.index)
  if (end === -1) return err({ kind: 'unterminatedComment', index: start })
  const body = cursor.text.slice(cursor.index, end)
  cursor.index = end + 1
  return ok(body.replace(/\s+/g, ' ').trim())
}

const readHeader = (cursor: Cursor): Result<readonly [string, string], PgnError> => {
  const start = cursor.index
  cursor.index += 1 // consume '['

  while (cursor.text[cursor.index] === ' ') cursor.index += 1
  let name = ''
  while (cursor.index < cursor.text.length && /[^\s"\]]/.test(cursor.text[cursor.index] as string)) {
    name += cursor.text[cursor.index]
    cursor.index += 1
  }

  while (cursor.text[cursor.index] === ' ') cursor.index += 1
  if (cursor.text[cursor.index] !== '"') return err({ kind: 'unterminatedHeader', index: start })
  cursor.index += 1

  // Values may contain \" and \\, so this cannot be a plain indexOf.
  let value = ''
  for (;;) {
    const char = cursor.text[cursor.index]
    if (char === undefined) return err({ kind: 'unterminatedHeader', index: start })
    if (char === '\\') {
      const next = cursor.text[cursor.index + 1]
      if (next === '"' || next === '\\') {
        value += next
        cursor.index += 2
        continue
      }
    }
    if (char === '"') {
      cursor.index += 1
      break
    }
    value += char
    cursor.index += 1
  }

  while (cursor.text[cursor.index] === ' ') cursor.index += 1
  if (cursor.text[cursor.index] !== ']') return err({ kind: 'unterminatedHeader', index: start })
  cursor.index += 1

  return ok([name, value] as const)
}

/** A run of characters that could be a move, a move number, a NAG, or a result. */
const readWord = (cursor: Cursor): string => {
  const start = cursor.index
  while (cursor.index < cursor.text.length && !/[\s{}();%[\]]/.test(cursor.text[cursor.index] as string)) {
    cursor.index += 1
  }
  return cursor.text.slice(start, cursor.index)
}

const MOVE_NUMBER = /^\d+\.*$/
const NAG = /^\$\d+$/

interface Movetext {
  readonly nodes: PgnNode[]
  preComment: string | null
  result: GameResult | null
}

const attachComment = (nodes: PgnNode[], text: Movetext, comment: string): void => {
  const last = nodes.at(-1)
  if (last === undefined) {
    text.preComment = text.preComment === null ? comment : `${text.preComment} ${comment}`
    return
  }
  nodes[nodes.length - 1] = {
    ...last,
    comment: last.comment === null ? comment : `${last.comment} ${comment}`,
  }
}

/**
 * Reads moves until the variation closes (`depth > 0`), a result token appears,
 * or the next game's tag section begins.
 */
const readMovetext = (cursor: Cursor, text: Movetext, nodes: PgnNode[], depth: number): Result<null, PgnError> => {
  for (;;) {
    skipTrivia(cursor)
    const char = cursor.text[cursor.index]
    if (char === undefined) {
      return depth > 0 ? err({ kind: 'unterminatedVariation', index: cursor.index }) : ok(null)
    }

    if (char === '[') {
      // A tag pair at this point belongs to the next game.
      return depth > 0 ? err({ kind: 'unterminatedVariation', index: cursor.index }) : ok(null)
    }

    if (char === '{') {
      const comment = readBraceComment(cursor)
      if (!comment.ok) return comment
      attachComment(nodes, text, comment.value)
      continue
    }

    if (char === '(') {
      const start = cursor.index
      cursor.index += 1
      const branch: PgnNode[] = []
      const inner = readMovetext(cursor, text, branch, depth + 1)
      if (!inner.ok) return inner
      if (cursor.text[cursor.index] !== ')') return err({ kind: 'unterminatedVariation', index: start })
      cursor.index += 1

      const last = nodes.at(-1)
      // A variation with nothing to branch from is malformed but harmless;
      // dropping it is friendlier than rejecting the whole game.
      if (last !== undefined) {
        nodes[nodes.length - 1] = { ...last, variations: [...last.variations, branch] }
      }
      continue
    }

    if (char === ')') {
      if (depth === 0) return err({ kind: 'unexpectedCharacter', character: ')', index: cursor.index })
      return ok(null)
    }

    if (char === ']') return err({ kind: 'unexpectedCharacter', character: ']', index: cursor.index })

    const word = readWord(cursor)
    if (word === '') {
      return err({ kind: 'unexpectedCharacter', character: char, index: cursor.index })
    }
    if (MOVE_NUMBER.test(word) || word === '...') continue
    if (NAG.test(word)) {
      const last = nodes.at(-1)
      if (last !== undefined) {
        nodes[nodes.length - 1] = { ...last, nags: [...last.nags, Number.parseInt(word.slice(1), 10)] }
      }
      continue
    }
    if ((RESULTS as readonly string[]).includes(word)) {
      if (depth === 0) {
        text.result = word as GameResult
        return ok(null)
      }
      continue
    }

    nodes.push({ san: word, nags: [], comment: null, variations: [] })
  }
}

export const parsePgnGames = (source: string): Result<readonly PgnGame[], PgnError> => {
  const cursor: Cursor = { text: source, index: 0 }
  const games: PgnGame[] = []

  for (;;) {
    skipTrivia(cursor)
    if (cursor.index >= source.length) break

    const headers = new Map<string, string>()
    for (;;) {
      skipTrivia(cursor)
      if (cursor.text[cursor.index] !== '[') break
      const header = readHeader(cursor)
      if (!header.ok) return header
      headers.set(header.value[0], header.value[1])
    }

    const text: Movetext = { nodes: [], preComment: null, result: null }
    const parsed = readMovetext(cursor, text, text.nodes, 0)
    if (!parsed.ok) return parsed

    if (headers.size === 0 && text.nodes.length === 0 && text.result === null) break

    games.push({
      headers,
      preComment: text.preComment,
      moves: text.nodes,
      result: text.result ?? (headers.get('Result') as GameResult | undefined) ?? '*',
    })
  }

  return games.length === 0 ? err({ kind: 'noGames' }) : ok(games)
}

export const parsePgn = (source: string): Result<PgnGame, PgnError> => {
  const games = parsePgnGames(source)
  return games.ok ? ok(games.value[0] as PgnGame) : games
}

/**
 * Play a parsed game's mainline out on a real board, which is the only way to
 * know the movetext was actually coherent. Variations are kept in the AST but
 * not replayed.
 */
export const replayPgn = (pgn: PgnGame): Result<Game, PgnError> => {
  const setup = pgn.headers.get('FEN') ?? INITIAL_FEN
  const started = newGame(setup)
  if (!started.ok) return err({ kind: 'badSetupFen', fen: setup })

  let game = started.value
  for (const [ply, node] of pgn.moves.entries()) {
    if (node.san === '--' || node.san === 'Z0') return err({ kind: 'nullMoveUnsupported', ply })

    // Parse here rather than via `applySan` so the SAN error — ambiguous versus
    // illegal versus malformed — reaches the caller intact.
    const move = parseSan(game.position, node.san)
    if (!move.ok) return err({ kind: 'illegalMove', san: node.san, ply, cause: move.error })

    const advanced = applyMove(game, move.value)
    if (!advanced.ok) return err({ kind: 'illegalMove', san: node.san, ply, cause: { kind: 'illegal', text: node.san } })
    game = advanced.value
  }
  return ok(game)
}

export const parseAndReplay = (source: string): Result<Game, PgnError> => {
  const parsed = parsePgn(source)
  return parsed.ok ? replayPgn(parsed.value) : parsed
}

const escapeHeader = (value: string): string => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')

const SEVEN_TAG_ROSTER = ['Event', 'Site', 'Date', 'Round', 'White', 'Black', 'Result'] as const

const DEFAULT_HEADERS: Readonly<Record<string, string>> = {
  Event: 'Casual game',
  Site: '?',
  Date: '????.??.??',
  Round: '-',
  White: '?',
  Black: '?',
}

/** Wrap at the 80-column limit the PGN spec asks for. */
const wrap = (tokens: readonly string[]): string => {
  const lines: string[] = []
  let line = ''
  for (const token of tokens) {
    if (line === '') line = token
    else if (line.length + 1 + token.length <= 80) line += ` ${token}`
    else {
      lines.push(line)
      line = token
    }
  }
  if (line !== '') lines.push(line)
  return lines.join('\n')
}

export const movetextOf = (game: Game): string => {
  const tokens: string[] = []
  let before = game.initial

  for (const entry of game.history) {
    // A game that starts with Black to move opens "1... e5", not "1. e5".
    if (before.turn === WHITE) tokens.push(`${before.fullmoves}.`)
    else if (tokens.length === 0) tokens.push(`${before.fullmoves}...`)
    tokens.push(entry.san)
    before = entry.after
  }

  tokens.push(resultOf(game.status))
  return wrap(tokens)
}

export const serializePgn = (game: Game, headers: Readonly<Record<string, string>> = {}): string => {
  const merged = new Map<string, string>(Object.entries({ ...DEFAULT_HEADERS, ...headers }))
  merged.set('Result', resultOf(game.status))
  if (game.initialFen !== INITIAL_FEN) {
    merged.set('SetUp', '1')
    merged.set('FEN', game.initialFen)
  }

  const ordered = [
    ...SEVEN_TAG_ROSTER.filter((tag) => merged.has(tag)).map((tag) => [tag, merged.get(tag) as string] as const),
    ...[...merged].filter(([tag]) => !(SEVEN_TAG_ROSTER as readonly string[]).includes(tag)),
  ]

  const tags = ordered.map(([tag, value]) => `[${tag} "${escapeHeader(value)}"]`).join('\n')
  return `${tags}\n\n${movetextOf(game)}\n`
}
